import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { MatDialog, MatDialogModule } from '@angular/material/dialog';

import Resources from '@constants/resourses';
import { environment } from '@environments/environment';

import { SaveMoneyEnergyBillsComponent } from '../save-money-energy-bills/save-money-energy-bills.component';
import { LoginComponent } from '@shared/dialog/login/login.component';

import { SessionService } from '@services/session.service';
import { RestapiService } from '@services/restapi.service';
import { AppService } from '@services/app.service';
import { UtilityService } from '@services/utility.service';
import { IErrorHandling } from 'src/app/core/models/utility.interface';

@Component({
  selector: 'app-authenticate',
  standalone: true,
  imports: [SaveMoneyEnergyBillsComponent, LoginComponent, MatDialogModule],
  templateUrl: './authenticate.component.html',
  styleUrl: './authenticate.component.scss'
})
export class AuthenticateComponent implements OnDestroy {
  subscriptions: Subscription[] = [];
  isLoading: boolean = true;
  isExpired: boolean = false;
  correlationId: string = '';

  constructor(
    private activatedRoute: ActivatedRoute,
    private sessionService: SessionService,
    private restAPI: RestapiService,
    private router: Router,
    public dialog: MatDialog,
    private appService: AppService,
    private utilityService: UtilityService
  ) {
    this.correlationId = this.activatedRoute.snapshot.queryParams?.correlationId || '';

    this.subscriptions.push(
      this.activatedRoute.params.subscribe((params) => {
        // Clear local and session storage
        this.sessionService.removeData(Resources.SessionStorageKeys.Email);
        this.sessionService.removeItemFromLocal(Resources.SessionStorageKeys.Postcode);
        this.sessionService.removeData(Resources.SessionStorageKeys.IsConsentApproval);
        this.sessionService.removeItemFromLocal(Resources.SessionStorageKeys.IsLoggedIn);
        this.sessionService.removeData(Resources.SessionStorageKeys.QuotesData);
        this.sessionService.clearData();
        this.sessionService.clearSessionStorage();
        if (params['signature']) {
          // Validate login request
          this.subscriptions.push(
            this.restAPI.getAPI(`/v1/users/validateLoginRequest/${params['signature']}`).subscribe({
              next: (data) => {
                // If success then set flags to identified loggedin user
                if (data?.resultStatus === 'success') {
                  this.sessionService.removeItemFromSession(Resources.SessionStorageKeys.CorrelationId);
                  // Set flags in local storage
                  this.sessionService.setData(Resources.SessionStorageKeys.Email, data?.data?.email);
                  this.sessionService.setItemIntoSession(Resources.SessionStorageKeys.Identifier, data?.data?.token);
                  this.sessionService.setData(Resources.SessionStorageKeys.IsConsentApproval, true);
                  this.sessionService.setData(Resources.SessionStorageKeys.IsAccessToken, true);

                  // Smart Meter Consent
                  this.sessionService.setItemIntoSession(Resources.SessionStorageKeys.IsNotNow, true);

                  if (this.correlationId) {
                    // Update sessionData with user state
                    this.subscriptions.push(
                      this.utilityService.updateUserLoggedInStatus(this.correlationId, true)
                    );
                    // Restore session data
                    this.sessionService.restoreSessionData(this.correlationId).subscribe({
                      next: (response) => {
                        this.sessionService.setItemIntoSession(Resources.SessionStorageKeys.CorrelationId, this.correlationId);
                        this.sessionService.setData(Resources.SessionStorageKeys.IsTopQuote, true);
                        this.appService.updateApplicationStatus('update');
                        // Redirect to energy result page with correlationId
                        this.router.navigate(['/energy-result'], {
                          queryParams: { correlationId: this.correlationId }
                        });
                      }, error: (error) => {
                        // Handle error during session restoration
                        console.error('Error restoring session data:', error);
                      }
                    });
                  } else {
                    // Redirect to information page
                    this.router.navigate(['/information'], { queryParams: this.activatedRoute.snapshot.queryParams });
                  }

                } else {
                  // Route change to link expired page
                  this.isExpired = true;
                  this.router.navigate(['/link-expired'], { queryParams: this.activatedRoute.snapshot.queryParams });
                }
              }, error: (error) => {
                // Redirect to service unavailable page
                this.router.navigate(['/service-unavailable'], { queryParams: this.activatedRoute.snapshot.queryParams });
              }
            })
          );
        } else {
          // Handle case when signature is not present
          this.isExpired = true;
        }
      })
    );

    // Check if correlationId is present in query params
    if (this.correlationId && !this.activatedRoute.snapshot.params['signature']) {
      this.signIn();
    }
  }

  // Function to handle sign-in logic
  // signIn method
  signIn(): void {
    const dialogRef = this.dialog.open(LoginComponent, {
      data: {
        placement: this.correlationId ? 'energyResult_sign_in' : 'linkExpire_sign_in',
        target: this.correlationId ? 'energyResult' : 'login',
        correlationId: this.correlationId || ''
      },
      width: '570px',
      maxWidth: window.innerWidth <= 767 ? '95vw' : '569px',
      disableClose: true,
      autoFocus: false,
      panelClass: 'ctm-modal'
    });

    // After the dialog is closed, check the result
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // user signed in
      }
    });
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }
}